import { PeoplePageConfigurationResponse } from "@/feature/protected/people/people.service";
import { apiClientSecure } from '@/lib/api/index';

/**
 * Service to manage menu item visibility based on various conditions
 * Centralizes all logic for showing/hiding menu items
 * Generic approach - can be extended for any step
 */
class MenuVisibilityService {
  private static instance: MenuVisibilityService;
  
  // Generic map to track visibility of each step by pagename
  private stepVisibility: Map<string, boolean> = new Map();
  
  // Flag to suppress events during batch updates
  private suppressEvents: boolean = false;

  private constructor() {
    // Private constructor for singleton pattern
    // Initialize with default visibility (all visible)
  }

  /**
   * Get singleton instance
   */
  public static getInstance(): MenuVisibilityService {
    if (!MenuVisibilityService.instance) {
      MenuVisibilityService.instance = new MenuVisibilityService();
    }
    return MenuVisibilityService.instance;
  }

  /**
   * Get visibility status for a specific step
   * @param stepName - The step pagename (e.g., 'people', 'pets', etc.)
   * @returns true if step should be shown, false otherwise (defaults to true if not set)
   */
  public getStepVisibility(stepName: string): boolean {
    const normalizedName = stepName.toLowerCase();
    return this.stepVisibility.get(normalizedName) ?? true;
  }

  /**
   * Set visibility for a specific step
   * @param stepName - The step pagename
   * @param visible - Whether the step should be visible
   * @param dispatchEvent - Whether to dispatch the visibility changed event (default true)
   */
  public setStepVisibility(stepName: string, visible: boolean, dispatchEvent: boolean = true): void {
    const normalizedName = stepName.toLowerCase();
    this.stepVisibility.set(normalizedName, visible);
    
    // Dispatch generic event to notify menu component of visibility change
    // Skip if events are suppressed (during batch updates)
    if (dispatchEvent && !this.suppressEvents) {
      window.dispatchEvent(new CustomEvent('stepVisibilityChanged', {
        detail: { 
          stepName: normalizedName,
          shouldShow: visible,
          source: `MenuVisibilityService-${normalizedName}`
        }
      }));
    }
    
    console.log(`🔧 MenuVisibilityService: Step "${normalizedName}" visibility updated:`, visible);
  }

  /**
   * Get people page configuration from API
   */
  async getPeoplePageConfiguration(): Promise<PeoplePageConfigurationResponse> {
    const connecturl = `people/getpageconfiguration`;
    const response = await apiClientSecure.get(connecturl);
    return response.data;
  }

  /**
   * Update visibility for all steps that require API checks
   * This is the main method to refresh all step visibilities
   * Events are suppressed during batch update to prevent cascading refreshes
   */
  public async updateAllStepVisibility(): Promise<void> {
    // Suppress events during batch update
    this.suppressEvents = true;
    
    try {
      // Update people section visibility
      await this.updatePeopleVisibility();
      
      // Add more step visibility updates here as needed
      // await this.updatePetsVisibility();
      // await this.updateVehiclesVisibility();
      
      console.log('🔧 MenuVisibilityService: All step visibilities updated');
    } finally {
      // Re-enable events
      this.suppressEvents = false;
    }
  }

  /**
   * Update People section visibility from API
   */
  public async updatePeopleVisibility(): Promise<void> {
    try {
      const response = await this.getPeoplePageConfiguration();
      const shouldShow = response.showGuarantorSection || response.showNonLesseeSection || response.showaddroommate;
      this.setStepVisibility('people', shouldShow);
    } catch (error) {
      console.error('🔧 MenuVisibilityService: Failed to update people visibility:', error);
      // Default to showing if API fails
      this.setStepVisibility('people', true);
    }
  }

  /**
   * Check if a specific menu item should be visible
   * @param pagename - The menu item pagename (e.g., 'people', 'pets', etc.)
   * @returns true if the menu item should be visible, false otherwise
   */
  public shouldShowMenuItem(pagename: string): boolean {
    return this.getStepVisibility(pagename);
  }

  /**
   * Get the total count of visible menu items
   * @param menuItems - Array of menu items with pagename property
   * @returns Count of visible menu items
   */
  public getVisibleMenuItemsCount(menuItems: Array<{ pagename: string; isHidden?: boolean }>): number {
    return menuItems.filter(item => {
      if (item.isHidden) return false;
      return this.shouldShowMenuItem(item.pagename);
    }).length;
  }

  /**
   * Clear all visibility settings - useful for logout or reset
   * Resets to default state (all sections visible)
   */
  public clearVisibilitySettings(): void {
    this.stepVisibility.clear();
    console.log('🔧 MenuVisibilityService: Cleared all visibility settings (reset to defaults)');
  }
}

// Export singleton instance
export const menuVisibilityService = MenuVisibilityService.getInstance();
